import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Loader2, MapPin, CalendarClock, PartyPopper, ImagePlus } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Gathering",
  "Meeting",
  "Concert",
  "Ceilidh",
  "Food Event",
  "Highland Games",
  "Reunion",
  "Other",
];

function fmtRange(startV, endV) {
  if (!startV) return "";
  try {
    const dOpts = { year: "numeric", month: "short", day: "numeric" };
    const tOpts = { hour: "2-digit", minute: "2-digit" };
    const s = new Date(startV);
    const sDate = s.toLocaleDateString(undefined, dOpts);
    const sTime = s.toLocaleTimeString(undefined, tOpts);
    if (!endV) return `${sDate}, ${sTime}`;
    const e = new Date(endV);
    if (s.toDateString() === e.toDateString()) {
      return `${sDate} · ${sTime} – ${e.toLocaleTimeString(undefined, tOpts)}`;
    }
    return `${sDate} – ${e.toLocaleDateString(undefined, dOpts)}`;
  } catch {
    return startV;
  }
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ type: EVENT_TYPES[0], description: "", country: "", city: "", event_at: "", event_end: "", image_url: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/events`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      // Show soonest events first (fall back to submission order if no date).
      list.sort((a, b) => (a.event_at || a.created_at || "").localeCompare(b.event_at || b.created_at || ""));
      setEvents(list);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim() || !form.country.trim() || !form.city.trim() || !form.event_at) {
      toast.error("Please fill in the type, date & time, description, country and city.");
      return;
    }
    if (form.event_end && form.event_end < form.event_at) {
      toast.error("The end date/time must be after the start.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/events/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Thank you — your event has been submitted for approval by the clan gathering.");
      setForm({ type: EVENT_TYPES[0], description: "", country: "", city: "", event_at: "", event_end: "", image_url: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Downscale + compress an uploaded image to a small JPEG data URL so it fits
  // comfortably in a single request (no separate storage needed at this scale).
  const handleUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1200;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
        if (dataUrl.length > 900000) {
          toast.error("That image is too large — please try a smaller one or paste a link.");
          return;
        }
        setForm((f) => ({ ...f, image_url: dataUrl }));
        toast.success("Photo added.");
      };
      img.onerror = () => toast.error("Could not read that image.");
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const field =
    "w-full rounded-md border border-clan-ink/10 bg-clan-ink/[0.04] px-4 py-3 font-sans text-clan-ink placeholder:text-clan-muted/70 transition-colors duration-300 focus:border-clan-accent focus:bg-clan-ink/[0.06] focus:outline-none focus:ring-1 focus:ring-clan-accent";

  return (
    <div className="relative min-h-screen bg-clan-bg text-clan-ink">
      <div className="grain-overlay" aria-hidden="true" />
      <Toaster position="top-center" theme="dark" />
      <Header />

      <main className="mx-auto max-w-6xl px-6 pb-28 pt-32 lg:px-10 lg:pt-40">
        {/* Hero */}
        <motion.div
          data-testid="events-hero"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-clan-accent">
            The Clan Gathers
          </span>
          <h1 className="mt-6 font-serif text-4xl font-light leading-[1.02] tracking-tight text-clan-ink sm:text-5xl lg:text-6xl pearl">
            Clan Events
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-clan-muted">
            Meetings, concerts, ceilidhs and food nights across the world — wherever the McDonalds gather. Announce your
            own below; every submission is reviewed before it appears here.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-14 lg:grid-cols-[1fr_360px] lg:gap-16">
          {/* Approved events */}
          <div data-testid="events-list">
            {loading ? (
              <div className="grid place-items-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-clan-accent" />
              </div>
            ) : events.length === 0 ? (
              <div className="rounded-lg border border-dashed border-clan-ink/15 p-14 text-center">
                <PartyPopper className="mx-auto mb-4 h-7 w-7 text-clan-accent/70" />
                <p className="font-serif text-2xl italic text-clan-muted">
                  No events yet — be the first to announce a gathering.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {events.map((ev) => (
                  <motion.article
                    key={ev.id}
                    data-testid={`event-item-${ev.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5 }}
                    className="group overflow-hidden rounded-lg border border-clan-ink/10 bg-clan-surface transition-colors duration-300 hover:border-clan-accent/40"
                  >
                    {ev.image_url && (
                      <img
                        src={ev.image_url}
                        alt={`${ev.type} in ${ev.city}`}
                        loading="lazy"
                        className="h-48 w-full object-cover"
                      />
                    )}
                    <div className="p-6">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="rounded-md border border-clan-accent/50 bg-clan-accent/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-clan-accent">
                        {ev.type}
                      </span>
                      <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-clan-muted">
                        <MapPin className="h-3.5 w-3.5 text-clan-accent" /> {ev.city}, {ev.country}
                      </span>
                      {ev.event_at && (
                        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-clan-accent">
                          <CalendarClock className="h-3.5 w-3.5" /> {fmtRange(ev.event_at, ev.event_end)}
                        </span>
                      )}
                    </div>
                    <p className="mt-3 text-base leading-relaxed text-clan-ink/85">{ev.description}</p>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </div>

          {/* Submit form */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <form
              onSubmit={submit}
              data-testid="event-submit-form"
              className="rounded-lg border border-clan-ink/10 bg-clan-surface p-6"
            >
              <h2 className="font-serif text-2xl text-clan-ink">Announce an event</h2>
              <p className="mt-1 mb-5 text-sm text-clan-muted">Shared once approved by the clan gathering.</p>
              <div className="flex flex-col gap-3">
                <select
                  data-testid="event-type-select"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className={field}
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    data-testid="event-city-input"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="City"
                    className={field}
                  />
                  <input
                    data-testid="event-country-input"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    placeholder="Country"
                    className={field}
                  />
                </div>
                <textarea
                  data-testid="event-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What is happening? Venue and details…"
                  rows={4}
                  className={field}
                />
                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-clan-muted">
                  Date &amp; time
                  <input
                    data-testid="event-datetime-input"
                    type="datetime-local"
                    value={form.event_at}
                    onChange={(e) => setForm({ ...form, event_at: e.target.value })}
                    className={`${field} mt-1.5`}
                  />
                </label>
                <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-clan-muted">
                  End (optional — for multi-day)
                  <input
                    data-testid="event-end-input"
                    type="datetime-local"
                    value={form.event_end}
                    min={form.event_at || undefined}
                    onChange={(e) => setForm({ ...form, event_end: e.target.value })}
                    className={`${field} mt-1.5`}
                  />
                </label>

                {/* Photo: link or upload */}
                <input
                  data-testid="event-image-url-input"
                  value={form.image_url.startsWith("data:") ? "" : form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="Photo link (optional)"
                  className={field}
                />
                <div className="flex items-center gap-3">
                  <label
                    data-testid="event-image-upload-label"
                    className="flex cursor-pointer items-center gap-2 rounded-md border border-clan-ink/15 bg-clan-ink/[0.04] px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-clan-muted transition-colors hover:border-clan-accent/60 hover:text-clan-ink"
                  >
                    <ImagePlus className="h-3.5 w-3.5" /> Upload photo
                    <input
                      data-testid="event-image-file-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files?.[0])}
                    />
                  </label>
                  {form.image_url && (
                    <div className="flex items-center gap-2">
                      <img src={form.image_url} alt="preview" className="h-10 w-10 rounded object-cover" />
                      <button
                        type="button"
                        data-testid="event-image-clear-btn"
                        onClick={() => setForm({ ...form, image_url: "" })}
                        className="font-mono text-[10px] uppercase tracking-widest text-clan-muted hover:text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <button
                  data-testid="event-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="mt-1 flex items-center justify-center gap-2 rounded-md bg-clan-primary px-6 py-3.5 font-mono text-xs uppercase tracking-widest text-white transition-colors duration-300 hover:bg-clan-primaryHover focus:outline-none focus:ring-2 focus:ring-clan-accent disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {submitting ? "Submitting…" : "Submit event"}
                </button>
              </div>
            </form>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
